import { useState, useCallback, useEffect, useRef } from 'react';
import { SPARK_CONFIG } from '../constants/sparkConfig';

export const useSparkAnimation = (isNewHexagon: boolean, isEmpty: boolean) => {
  const [showSpark, setShowSpark] = useState<boolean>(false);
  const [sparkKey, setSparkKey] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const scheduleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const scheduleNextSpark = useCallback(() => {
    if (isAnimating) return;
    
    if (scheduleTimeoutRef.current) {
      clearTimeout(scheduleTimeoutRef.current);
    }
    
    const delay = SPARK_CONFIG.MIN_BETWEEN_DELAY + 
      Math.random() * (SPARK_CONFIG.MAX_BETWEEN_DELAY - SPARK_CONFIG.MIN_BETWEEN_DELAY);
    
    scheduleTimeoutRef.current = setTimeout(() => {
      if (isNewHexagon && !isAnimating) {
        setShowSpark(true);
        setIsAnimating(true);
        setSparkKey(prev => prev + 1);
      }
    }, delay);
  }, [isNewHexagon, isAnimating]);

  const handleSparkAnimationEnd = useCallback(() => {
    setShowSpark(false);
    setTimeout(() => {
      setIsAnimating(false);
      scheduleNextSpark();
    }, SPARK_CONFIG.AFTER_ANIMATION_PAUSE);
  }, [scheduleNextSpark]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (isNewHexagon && !isEmpty && !showSpark && !isAnimating) {
      const delay = SPARK_CONFIG.MIN_INITIAL_DELAY + 
        Math.random() * (SPARK_CONFIG.MAX_INITIAL_DELAY - SPARK_CONFIG.MIN_INITIAL_DELAY);
      
      timeoutId = setTimeout(() => {
        setShowSpark(true);
        setIsAnimating(true);
        setSparkKey(prev => prev + 1);
      }, delay);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isNewHexagon, isEmpty, showSpark, isAnimating]);

  useEffect(() => {
    return () => {
      if (scheduleTimeoutRef.current) {
        clearTimeout(scheduleTimeoutRef.current);
      }
    };
  }, []);

  return {
    showSpark,
    sparkKey,
    handleSparkAnimationEnd
  };
};